/**
 * Squares ball result filtering and display. Must stay in sync with backend squares-settlement-common.js.
 * - Ignore: 1W–7W, W+1w–W+5w, 1NB–8NB, wides (wide, Wd, wide+4, etc.), anything containing "Pen".
 * - Ignore Decimal event-status strings mistakenly stored as ball results (e.g. "Ball In Progress").
 * - Display mapping: 0–7, 1B–7B, 1LB–7LB → 0–7; W → "W"; W+1–W+5 → 1–5.
 */

/**
 * @param {string|number|null|undefined} raw
 * @returns {boolean}
 */
export function isEventStatusPlaceholderBallResult(raw) {
  if (raw == null || String(raw).trim() === "") return false;
  const n = String(raw)
    .toLowerCase()
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (n.includes("ball in progress")) return true;
  if (n.includes("match stable")) return true;
  if (n.includes("runs likely")) return true;
  if (n.includes("possible wicket")) return true;
  if (n.includes("unknown event")) return true;
  if (n.includes("umpire review")) return true;
  if (n.includes("data assembly")) return true;
  if (n.includes("break in play")) return true;
  if (n.includes("match scheduled")) return true;
  if (n.includes("match complete")) return true;
  if (n.includes("event closed")) return true;
  if (n.includes("ejected")) return true;
  if (n === "super over") return true;
  return false;
}

/**
 * Six stored values that are all event-status placeholders (bad delta settle) — treat as no ball_results.
 * @param {unknown} arr
 * @returns {boolean}
 */
export function ballResultsAreOnlyEventPlaceholders(arr) {
  if (!Array.isArray(arr) || arr.length < 6) return false;
  return arr.slice(0, 6).every((v) => isEventStatusPlaceholderBallResult(v));
}

/**
 * For UI/API: real ball_results array, or null if missing / placeholder-only (so we refetch and show empty slots).
 * @param {unknown} ballResults
 * @returns {string[]|null}
 */
export function effectiveSquaresBallResults(ballResults) {
  if (!Array.isArray(ballResults) || ballResults.length === 0) return null;
  if (ballResultsAreOnlyEventPlaceholders(ballResults)) return null;
  return ballResults;
}

/**
 * @param {string|number|null|undefined} raw
 * @returns {boolean}
 */
export function isIgnoreBallResult(raw) {
  if (raw == null || String(raw).trim() === "") return true;
  const s = String(raw).trim();
  const lower = s.toLowerCase();
  if (isEventStatusPlaceholderBallResult(s)) return true;
  if (lower.includes("pen")) return true;
  if (lower.includes("wide") || /(^|[^a-z])wd([^a-z]|$)/.test(lower)) return true; // wd, 1wd, wd+1
  if (/^[1-7]w$/i.test(lower)) return true;
  if (/^w\+[1-5]w$/i.test(lower)) return true; // W+1w, W+2w, etc. (wicket+run with trailing w)
  if (/^[1-8]nb$/i.test(lower)) return true;
  if (/no\s*ball|(^|[^a-z])nb([^a-z]|$)/.test(lower)) return true; // nb+1, 1 nb, no ball
  return false;
}

/**
 * Filter raw delivery list to only "counted" balls (non-ignore). Returns up to 6 values.
 * @param {Array<string|number|null|undefined>} rawArray
 * @returns {string[]}
 */
export function filterToCountedBallResults(rawArray) {
  if (!Array.isArray(rawArray)) return [];
  const out = [];
  for (const v of rawArray) {
    if (v == null || String(v).trim() === "") continue;
    if (isIgnoreBallResult(v)) continue;
    out.push(String(v).trim());
    if (out.length >= 6) break;
  }
  return out;
}

/**
 * Map raw result to display string for ball boxes. Returns null for ignore.
 * @param {string|number|null|undefined} raw
 * @returns {string|null} "0"–"7" or "W"
 */
export function displayBallResult(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  if (isIgnoreBallResult(raw)) return null;
  const s = String(raw).trim();
  const lower = s.toLowerCase();
  if (lower === "w" || lower === "wicket" || lower === "wkt") return "W";
  if (lower === "w+1" || lower === "wicket+1" || lower === "wkt+1") return "1";
  if (lower === "w+2" || lower === "wicket+2" || lower === "wkt+2") return "2";
  if (lower === "w+3" || lower === "wicket+3" || lower === "wkt+3") return "3";
  if (lower === "w+4" || lower === "wicket+4" || lower === "wkt+4") return "4";
  if (lower === "w+5" || lower === "wicket+5" || lower === "wkt+5") return "5";
  if (lower === "no run" || lower === "0 runs" || lower === "dot" || lower === "no runs") return "0";
  if (lower === "1lb" || lower === "1b" || s === "1") return "1";
  if (lower === "2lb" || lower === "2b" || s === "2") return "2";
  if (lower === "3lb" || lower === "3b" || s === "3") return "3";
  if (lower === "4lb" || lower === "4b" || s === "4") return "4";
  if (lower === "5lb" || lower === "5b" || s === "5") return "5";
  if (lower === "6lb" || lower === "6b" || s === "6") return "6";
  if (lower === "7lb" || lower === "7b" || s === "7") return "7";
  const n = parseInt(s, 10);
  if (Number.isFinite(n) && n >= 0 && n <= 7) return String(n);
  return null;
}

/**
 * Label for My Zone summary white outcome cells (wicket spelled out for narrow tiles).
 * @param {string|null|undefined} displayVal - output of {@link displayBallResult} or padded "–"
 * @returns {string}
 */
export function squaresOutcomeSummaryLabel(displayVal) {
  const s = String(displayVal ?? "–").trim();
  if (s === "–" || s === "") return "–";
  if (s.toUpperCase() === "W") return "WICKET";
  return s;
}

/**
 * From raw over delivery list, get the display values for the 6 ball boxes (only counted balls; ignore values are skipped).
 * @param {Array<string|number|null|undefined>} rawOverDelivery
 * @returns {string[]} Length 0–6
 */
export function getDisplayBallResults(rawOverDelivery) {
  const counted = filterToCountedBallResults(rawOverDelivery || []);
  return counted.map((raw) => displayBallResult(raw)).filter((v) => v != null);
}

/** Run labels used for selection comparison (must match backend RUN_LABELS). */
const RUN_LABELS = ["0", "1", "2", "3", "4", "5+"];

/**
 * Normalise display result to run label for comparison to user selection. Matches backend normaliseBallResult logic.
 * Used to compute win/lose on frontend as soon as we have the ball result, without waiting for backend per_ball.
 * @param {string} displayResult - Display value from result box: "0"–"7" or "W"
 * @returns {string|null} One of '0'|'1'|'2'|'3'|'4'|'5+' or null
 */
export function normaliseDisplayResultToRunLabel(displayResult) {
  if (displayResult == null || String(displayResult).trim() === "") return null;
  const s = String(displayResult).trim();
  const upper = s.toUpperCase();
  if (upper === "W") return "0";
  const lower = s.toLowerCase();
  if (lower === "5+" || lower === "5plus") return "5+";
  // Match backend normaliseBallResult: only selection buckets 0–4 and 5+; 5 / 6 / 7 runs all map to 5+.
  if (["5", "6", "7"].includes(s)) return "5+";
  if (RUN_LABELS.includes(s)) return s;
  const n = parseInt(s, 10);
  if (Number.isFinite(n) && n >= 5) return "5+";
  if (Number.isFinite(n) && n >= 0 && n <= 4) return String(n);
  return null;
}

/**
 * Compute whether the user's selection matched the ball result. Use when we have result + prediction but backend per_ball not yet.
 * @param {string} displayResult - Ball result display value ("0"–"7", "W")
 * @param {string} selectedRun - User's selection run label ("0","1","2","3","4","5+")
 * @returns {boolean|null} true = win, false = lose, null = can't compute
 */
export function computeBallWon(displayResult, selectedRun) {
  const normalised = normaliseDisplayResultToRunLabel(displayResult);
  if (normalised == null || !selectedRun || !RUN_LABELS.includes(String(selectedRun))) return null;
  return normalised === String(selectedRun);
}

/**
 * Effective run label from a selection object (matches backend getSelectionRun).
 * @param {object|null|undefined} sel
 * @returns {string|null}
 */
export function getSelectionRun(sel) {
  if (!sel) return null;
  const runStr = sel.run != null ? String(sel.run).trim() : "";
  if (runStr && RUN_LABELS.includes(runStr)) return runStr;
  const labelStr = sel.runLabel != null ? String(sel.runLabel).trim() : "";
  if (labelStr && RUN_LABELS.includes(labelStr)) return labelStr;
  return null;
}

/**
 * Raw feed ball → selection bucket for comparison (matches backend normaliseBallResult).
 * @param {*} actual
 * @returns {string|null}
 */
export function normaliseBallResult(actual) {
  if (actual == null || actual === "") return null;
  if (isIgnoreBallResult(actual)) return null;
  const s = String(actual).trim();
  const lower = s.toLowerCase();
  if (lower === "w" || lower === "wicket" || lower === "wkt") return "0";
  if (lower === "w+1" || lower === "wicket+1" || lower === "wkt+1") return "1";
  if (lower === "w+2" || lower === "wicket+2" || lower === "wkt+2") return "2";
  if (lower === "w+3" || lower === "wicket+3" || lower === "wkt+3") return "3";
  if (lower === "w+4" || lower === "wicket+4" || lower === "wkt+4") return "4";
  if (lower === "w+5" || lower === "wicket+5" || lower === "wkt+5") return "5+";
  if (lower === "no run" || lower === "0 runs" || lower === "dot" || lower === "no runs") return "0";
  if (lower === "1lb" || lower === "1b" || s === "1") return "1";
  if (lower === "2lb" || lower === "2b" || s === "2") return "2";
  if (lower === "3lb" || lower === "3b" || s === "3") return "3";
  if (lower === "4lb" || lower === "4b" || s === "4") return "4";
  if (lower === "5lb" || lower === "5b" || s === "5") return "5+";
  if (lower === "6lb" || lower === "6b" || s === "6") return "5+";
  if (lower === "7lb" || lower === "7b" || s === "7") return "5+";
  if (lower === "5+" || lower === "5plus") return "5+";
  const n = parseInt(s, 10);
  if (Number.isFinite(n)) {
    if (n >= 5) return "5+";
    if (n >= 0 && n <= 4) return String(n);
  }
  return null;
}

/**
 * Per-ball cell state for leaderboard mini-boxes (squares_overs.winners[].selections vs ball_results).
 * @param {unknown} selections - JSON object or string; keys "1".."6"
 * @param {string[]|null|undefined} ballResultsRaw
 * @returns {Array<'won'|'lost'|'pending'>}
 */
export function sixBallLeaderboardCells(selections, ballResultsRaw) {
  let sel = selections;
  if (typeof sel === "string") {
    try {
      sel = JSON.parse(sel);
    } catch {
      sel = null;
    }
  }
  const obj = sel && typeof sel === "object" && !Array.isArray(sel) ? sel : {};
  const effective = effectiveSquaresBallResults(Array.isArray(ballResultsRaw) ? ballResultsRaw : null);
  const br = effective || [];
  const out = [];
  for (let i = 0; i < 6; i++) {
    if (i >= br.length) {
      out.push("pending");
      continue;
    }
    const expected = normaliseBallResult(br[i]);
    const run = getSelectionRun(obj[String(i + 1)]);
    if (expected == null) {
      out.push("pending");
      continue;
    }
    if (run == null) {
      out.push("lost");
      continue;
    }
    out.push(run === expected ? "won" : "lost");
  }
  return out;
}

/** 1-based ball in current over from feed delivery prefix in ballLabel (e.g. "3.4 Bowler to Batter"). */
export function squaresBallNumberFromBallLabel(ballLabel) {
  if (ballLabel == null || typeof ballLabel !== "string") return null;
  const m = ballLabel.trim().match(/^(\d+)\.(\d+)/);
  if (!m) return null;
  const ball = Number.parseInt(m[2], 10);
  if (!Number.isFinite(ball) || ball < 0 || ball > 6) return null;
  return ball === 0 ? 1 : ball;
}
